/**
 * Phone Accounts Component
 * Manage SIP accounts assigned to hardware
 */

import React, { useState } from 'react';
import {
  Box,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Switch,
  Typography,
  Paper,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  MenuItem,
  CircularProgress,
} from '@mui/material';
import {
  Add as AddIcon,
  Delete as DeleteIcon,
} from '@mui/icons-material';
import { hardwareService } from '../../services/hardwareService';
import type { Hardware } from './types';

interface PhoneAccountsProps {
  hardware: Hardware;
  onUpdate: () => void;
  customerId?: number;
}

const PhoneAccounts: React.FC<PhoneAccountsProps> = ({ hardware, onUpdate, customerId }) => {
  const [addModalOpen, setAddModalOpen] = useState(false);
  const [newExtension, setNewExtension] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleAddAccount = async () => {
    if (!newExtension) {
      setError('Please select an extension');
      return;
    }

    try {
      setLoading(true);
      setError(null);

      await hardwareService.addAccount(hardware.hardwareID, {
        extension: newExtension,
        enabled: true,
      }, customerId);

      setAddModalOpen(false);
      setNewExtension('');
      onUpdate();
    } catch (err: any) {
      setError(err.response?.data?.message || err.message || 'Failed to add account');
    } finally {
      setLoading(false);
    }
  };

  const handleToggleStatus = async (accountId: number, currentStatus: number) => {
    try {
      await hardwareService.updateAccount(accountId, currentStatus === 0, customerId);
      onUpdate();
    } catch (err: any) {
      setError(err.message || 'Failed to update account status');
    }
  };

  const handleDeleteAccount = async (accountId: number) => {
    if (!window.confirm('Are you sure you want to delete this account?')) {
      return;
    }

    try {
      await hardwareService.deleteAccount(accountId, customerId);
      onUpdate();
    } catch (err: any) {
      setError(err.message || 'Failed to delete account');
    }
  };

  const accounts = hardware.accounts || [];

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="h6">SIP Accounts</Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => setAddModalOpen(true)}
        >
          Add Account
        </Button>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {accounts.length === 0 ? (
        <Paper sx={{ p: 4, textAlign: 'center' }}>
          <Typography variant="body1" color="text.secondary" gutterBottom>
            No SIP accounts configured
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Add an account to assign an extension to a line button on this phone
          </Typography>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => setAddModalOpen(true)}
          >
            Add First Account
          </Button>
        </Paper>
      ) : (
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Line #</TableCell>
                <TableCell>Extension</TableCell>
                <TableCell>Enabled</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {accounts.map((account) => (
                <TableRow key={account.AccountID}>
                  <TableCell>{account.AcctLineNo}</TableCell>
                  <TableCell>{account.Extension}</TableCell>
                  <TableCell>
                    <Switch
                      checked={account['account.enable'] === 1}
                      onChange={() =>
                        handleToggleStatus(account.AccountID, account['account.enable'])
                      }
                      color="primary"
                    />
                  </TableCell>
                  <TableCell align="right">
                    <IconButton
                      size="small"
                      onClick={() => handleDeleteAccount(account.AccountID)}
                      color="error"
                    >
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* Add Account Modal */}
      <Dialog open={addModalOpen} onClose={() => setAddModalOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Add SIP Account</DialogTitle>
        <DialogContent>
          <TextField
            fullWidth
            select
            label="Extension"
            value={newExtension}
            onChange={(e) => setNewExtension(e.target.value)}
            sx={{ mt: 2 }}
            helperText="Select the extension to assign to this phone"
          >
            <MenuItem value="">Select Extension</MenuItem>
            {/* TODO: Load available extensions from API */}
            <MenuItem value="1001">1001</MenuItem>
            <MenuItem value="1002">1002</MenuItem>
            <MenuItem value="1003">1003</MenuItem>
          </TextField>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAddModalOpen(false)} disabled={loading}>
            Cancel
          </Button>
          <Button onClick={handleAddAccount} variant="contained" disabled={loading || !newExtension}>
            {loading ? <CircularProgress size={24} /> : 'Add Account'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default PhoneAccounts;
